
import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type BadgeVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'secondary'
  | 'sky'
  | 'amber'
  | 'pink'
  | 'emerald'
  | 'purple'
  | 'indigo';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  secondary: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
  sky: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  pink: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
};

const sizeStyles = {
  sm: 'px-2.5 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

export function Badge({
  children,
  variant = 'default',
  size = 'sm',
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {children}
    </span>
  );
}

// Utility function to get badge variant from role
export function getRoleBadgeVariant(role: string): BadgeVariant {
  switch (role.toLowerCase()) {
    case 'admin':
      return 'danger';
    case 'producer':
      return 'warning';
    case 'moderator':
      return 'info';
    default:
      return 'default';
  }
}

// Utility function to get badge variant from status
export function getStatusBadgeVariant(status: string): BadgeVariant {
  switch (status.toLowerCase()) {
    case 'active':
    case 'approved':
    case 'answered':
      return 'success';
    case 'pending':
      return 'warning';
    case 'inactive':
    case 'rejected':
    case 'dismissed':
      return 'danger';
    case 'archived': // Display label: Dismissed
    case 'displayed':
      return 'secondary';
    case 'queued':
      return 'info';
    default:
      return 'default';
  }
}

// Utility function to get badge variant from prayer category name
export function getCategoryBadgeVariant(category: string): BadgeVariant {
  switch (category.toLowerCase()) {
    case 'healing':
    case 'health':
    case 'relationships':
      return 'pink';
    case 'finances':
    case 'financial':
      return 'emerald';
    case 'family':
      return 'purple';
    case 'spiritual':
      return 'indigo';
    case 'work':
      return 'warning';
    case 'thanksgiving':
      return 'amber';
    case 'general':
    case 'other':
      return 'secondary';
    default:
      return 'secondary';
  }
}
